/**
 * Main application actions
 */

import { store, AppDispatch } from '../../../store';
import { logger } from '../../../helpers/logger';
import { appConstants, NotificationStatus, AppErrorCode } from './actionTypes';
import { Utilities } from '../../../helpers';
import { appApi } from '../../../services/appApi';
import { webdavApi } from '@sendent/nextcloud-api';
import {
    ShareLinkDialogResult,
    submitDialogResult,
} from '../../../services/dialogResults';
import { FileType, ServerFile, UploadFile, isServerFile } from '../../../model';
import {
    setFilesToUpload,
    setProgressSize,
    showNotification,
} from './actionCreators';
import { AppConfig } from '../../reducers/app/reducer';

/** Shape of the items nextcloud-api's webdav file listings actually return */
type WebdavFileStat = {
    filename: string;
    basename: string;
    type: FileType;
    etag: string;
    lastmod: string;
    mime?: string;
    size: number;
    props: { fileid?: number; getcontenttype?: string };
};

//////////////////////////////////////////////////////////////////////////
//
// Helper functions
//

const _initializeAddin = (payload: { appConfig: AppConfig }) => ({
    type: appConstants.APP_INITIALIZE_ADDIN,
    payload,
});

/**
 * Initializes the addin
 * @param inputLocation
 */
const initializeAddin = (_inputLocation: Location) => {
    return async (dispatch: AppDispatch) => {
        logger.info('initializeAddin action called', 'Init');
        const config = await appApi.getConfig();
        dispatch(_initializeAddin({ appConfig: config }));
    };
};

/**
 * Clears the notifications/errors
 */
const clearNotification = () => {
    return async (dispatch: AppDispatch) => {
        logger.info('The action clearNotification started');
        dispatch({ type: appConstants.APP_CLEAR_NOTIFICATION });
    };
};

/**
 * Get the server files
 */
const getServerFiles = (parentPath: string) => {
    return async (dispatch: AppDispatch): Promise<ServerFile[] | null> => {
        logger.info('The action getServerFiles', 'Files');
        const state = store.getState();
        const { user, userConfig } = state.authentication;
        if (!user || !userConfig) return null;

        const result = await webdavApi.getServerFiles(
            state.appReducer.appConfig.stubUrl,
            user,
            userConfig.userInfo.id,
            parentPath,
        );
        logger.debug(`files: ${JSON.stringify(result)}`, 'Files');
        const directoryFilesInfo = await webdavApi.getSharedFiles(
            state.appReducer.appConfig.stubUrl,
            user,
            userConfig.userInfo.id,
            parentPath,
        );
        logger.debug(`files2: ${JSON.stringify(directoryFilesInfo)}`, 'Files');

        const sharedFiles: string[] = [];
        if (directoryFilesInfo.success === true) {
            const parser = new DOMParser();
            const document = parser.parseFromString(
                String(directoryFilesInfo.result ?? ''),
                'text/xml',
            );

            const nodes = Utilities.getElementsByTagName(
                document,
                'd:response',
            );
            for (const file of nodes) {
                //filesId.push(Utilities.getElementTextContent(file))
                const href = Utilities.getElementByTagName(file, 'd:href');
                const mount = Utilities.getElementByTagName(
                    file,
                    'nc:mount-type',
                );
                if (href !== null && mount !== null) {
                    const mountGroup = Utilities.getElementTextContent(mount);
                    if (mountGroup === 'group') {
                        let name = decodeURIComponent(
                            Utilities.getElementTextContent(href),
                        );
                        logger.debug(
                            `name: ${name}, mountGroup: ${mountGroup}`,
                            'Files',
                        );
                        if (name.lastIndexOf('/') === name.length - 1)
                            name = name.substring(0, name.length - 1);
                        if (name.lastIndexOf('/') >= 0)
                            name = name.substring(name.lastIndexOf('/') + 1);
                        sharedFiles.push(name);
                    }
                }
            }
        }
        logger.debug(`sharedFiles: ${JSON.stringify(sharedFiles)}`, 'Files');

        if (result.success === true) {
            const serverFiles: ServerFile[] = [];
            let shared = false;
            (result.result ?? []).forEach((item: WebdavFileStat) => {
                if (item.type === 'directory') {
                    const folder = sharedFiles.find((file) => {
                        return file === item.basename;
                    });
                    if (!Utilities.isNANObject(folder)) shared = true;
                }
                const driveitem = {
                    fileId: item.props.fileid,
                    name: item.basename,
                    path: `${parentPath}/${item.basename}`,
                    parentPath: parentPath,
                    type: item.type,
                    etag: item.etag,
                    lastmod: item.lastmod,
                    contenttype: item.mime ?? item.props.getcontenttype,
                    contentlength: item.size,
                    selected: false,
                    shared: shared,
                };
                serverFiles.push(driveitem);
            });
            dispatch({
                type: appConstants.APP_SERVER_FILES,
                payload: { serverFiles: serverFiles },
            });
            return serverFiles;
        } else {
            // dispatch({ type:'app.APP_STOP_FETCH', payload: { } });
            dispatch({
                type: appConstants.APP_SHOW_NOTIFICATION,
                payload: {
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                    message: state.uiReducer.translate(
                        'load_serverfiles_error',
                    ),
                },
            });
            return null;
        }

        // dispatch({ type:'app.APP_STOP_FETCH', payload: { } });
        //}
    };
};

/**
 * Search the server files
 */
const searchServerFiles = (parentPath: string, mask: string) => {
    return async (dispatch: AppDispatch) => {
        logger.info('The action searchServerFiles', 'Files');
        const state = store.getState();
        const { user, userConfig } = state.authentication;
        if (!user || !userConfig) return;

        dispatch({
            type: appConstants.APP_SEARCH_RESULT,
            payload: { isSearch: true, isSearchComplete: false },
        });

        const result = await webdavApi.searchServerFiles(
            state.appReducer.appConfig.stubUrl,
            user,
            userConfig.userInfo.id,
            parentPath,
            mask,
        );

        const location = `/files/${userConfig.userInfo.id}${parentPath}`;
        if (result.success === true) {
            const serverFiles: ServerFile[] = [];
            (result.result ?? []).forEach((item: WebdavFileStat) => {
                let filename = item.filename;
                filename = filename.substring(
                    filename.indexOf(location) + location.length,
                );
                const folder = filename.substring(
                    0,
                    filename.indexOf(item.basename) - 1,
                );
                logger.debug(`filename: ${filename}`, 'Files');
                const driveitem = {
                    name: item.basename,
                    path: filename,
                    parentPath: `${parentPath}${folder}`,
                    type: item.type,
                    etag: item.etag,
                    lastmod: item.lastmod,
                    contenttype: item.mime,
                    contentlength: item.size,
                    selected: false,
                    shared: false,
                };
                if (item.basename !== userConfig.userInfo.id)
                    serverFiles.push(driveitem);
            });
            dispatch({
                type: appConstants.APP_SEARCH_RESULT,
                payload: {
                    isSearch: true,
                    isSearchComplete: true,
                    serverFiles: serverFiles,
                },
            });
        } else {
            // dispatch({ type:'app.APP_STOP_FETCH', payload: { } });
            dispatch({
                type: appConstants.APP_SHOW_NOTIFICATION,
                payload: {
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                    message: state.uiReducer.translate(
                        'load_serverfiles_error',
                    ),
                },
            });
            return;
        }
    };
};

/**
 * Create the new folder
 */
const createFolder = (parentPath: string, folder: string) => {
    return async (dispatch: AppDispatch) => {
        logger.info('The action createFolder', 'Files');
        const state = store.getState();
        const { user, userConfig } = state.authentication;
        if (!user || !userConfig) return;

        logger.debug(`parentPath: ${parentPath}`, 'Files');
        logger.debug(`folder: ${folder}`, 'Files');

        const exists = await webdavApi.checkFolderExist(
            state.appReducer.appConfig.stubUrl,
            user,
            userConfig.userInfo.id,
            parentPath + '/' + folder,
        );
        if (exists.success === true) {
            if (exists.result == false) {
                await webdavApi.createNewFolder(
                    state.appReducer.appConfig.stubUrl,
                    user,
                    userConfig.userInfo.id,
                    parentPath,
                    folder,
                );
            }
        }

        dispatch(getServerFiles(parentPath));
    };
};

/**
 * Create folders for share files by setting path
 */
export const createShareFolders = async (path: string) => {
    logger.info('The action createShareFolders', 'Share');
    const state = store.getState();
    const { user, userConfig } = state.authentication;
    if (!user || !userConfig) return false;

    const pathToShare = path.split('/');

    let curPath = '';
    let i = 0;
    while (i < pathToShare.length) {
        const item = pathToShare[i];
        const exists = await webdavApi.checkFolderExist(
            state.appReducer.appConfig.stubUrl,
            user,
            userConfig.userInfo.id,
            curPath + '/' + item,
        );
        if (exists.success === true) {
            if (exists.result == false)
                await webdavApi.createNewFolder(
                    state.appReducer.appConfig.stubUrl,
                    user,
                    userConfig.userInfo.id,
                    curPath,
                    item,
                );
        } else return false;
        curPath = curPath + '/' + item;
        i += 1;
    }
    return true;
};

/**
 * Upload selected files
 */
const shareLink = (files: UploadFile[]) => {
    return async (dispatch: AppDispatch) => {
        logger.info('The action shareLink', 'Share');
        const state = store.getState();
        const { user, userConfig } = state.authentication;
        if (!user || !userConfig) return;

        const file = files[0];
        // only uploaded files carry the server path the share link needs
        if (!file || !isServerFile(file)) {
            logger.error('shareLink: files[0] is not a server file', 'Share');
            dispatch(
                showNotification({
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                    message: state.uiReducer.translate(
                        'load_file_properties_error',
                    ),
                }),
            );
            return;
        }
        const filePath = file.path;
        const fileName = file.name;

        let url;
        logger.debug(`filePath: ${filePath}`, 'Share');

        const result = await webdavApi.getServerFileDetails(
            state.appReducer.appConfig.stubUrl,
            user,
            userConfig.userInfo.id,
            filePath,
        );
        if (result.success === true) {
            const parser = new DOMParser();
            const document = parser.parseFromString(
                String(result.result ?? ''),
                'text/xml',
            );
            const node = Utilities.getElementByTagName(document, 'oc:fileid');
            const fileId = Utilities.getElementTextContent(node);
            url = `${user.server}/index.php/f/${fileId}`;
        } else {
            dispatch({
                type: appConstants.APP_SHOW_NOTIFICATION,
                payload: {
                    status: NotificationStatus.Error,
                    errorCode: AppErrorCode.CustomError,
                    message: state.uiReducer.translate(
                        'load_file_properties_error',
                    ),
                },
            });
            return;
        }

        dispatch({
            type: appConstants.APP_TRANSFER_COMPLETED,
            payload: { transferCompleted: true },
        });
        logger.debug(
            `submit task before: url=${url}, fileName=${fileName}`,
            'Share',
        );
        submitDialogResult(new ShareLinkDialogResult({ url, name: fileName }));
        logger.debug('submit task after', 'Share');
    };
};

const clearFailedChunkedFileUploadData = () => {
    return async (dispatch: AppDispatch) => {
        dispatch(setFilesToUpload([]));
        dispatch(setProgressSize(0));
    };
};

export const appActions = {
    initializeAddin,
    clearNotification,
    getServerFiles,
    searchServerFiles,
    createFolder,
    shareLink,
    clearFailedChunkedFileUploadData,
};
